/**
 * Cabecera de la ficha de servidor en variante «tarjetas» (beta).
 *
 * Es solo presentación: no guarda estado ni llama a la API. Recibe los
 * contadores que ya calcula `ServerForm` y los pinta como chips, para que la
 * antigua smartbar de botones verticales desaparezca sin perder información.
 */
import React from 'react';
import { ServerStackIcon, PencilSquareIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';

function sep(children) {
  return children.filter(Boolean).reduce((acc, item, index) => {
    if (index === 0) return [item];
    return [...acc, ' › ', item];
  }, []);
}

export default function ServerFormCardsHeader({
  form,
  rangeLabel,
  chassisName,
  rolesCount = 0,
  disksCount = 0,
  nicsCount = 0,
  ipsCount = 0,
  accessCount = 0,
  usersCount = 0,
  lunsCount = 0,
  docButton = null,
  agentStatus = 'unknown',
  agentLastSeen = null,
  agentUpdate = null,
  firstScanUi = null,
  statusLabel = 'Activo',
  statusBg = 'rgba(34,197,94,.14)',
  statusColor = '#16a34a',
  envLabel = 'Producción',
  isExisting = false,
  onGo,
  onOpenDisks,
  onOpenUsers,
  editing = false,
  saving = false,
  isDirty = false,
  onEdit,
  onSave,
  onDiscard,
  // Título en línea (mismo patrón que la ficha canónica): si llega
  // `onTitleCommit`, el nombre se edita sin entrar en edición.
  onTitleChange,
  onTitleCommit,
  onTitleReset,
  titleDirty = false,
}) {
  const inline = typeof onTitleCommit === 'function';
  const showActions = inline ? (editing || titleDirty) : editing;
  const handleTitleKeyDown = (event) => {
    if (!inline) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();
      onTitleCommit?.();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      event.currentTarget.blur();
      onTitleReset?.();
    }
  };
  const ip = form.ip_address || '';
  const agenteInfo = agentStatus === 'online'
    ? { label: 'Online', color: '#22c55e' }
    : agentStatus === 'offline'
      ? { label: 'Offline', color: '#ef4444' }
      : { label: 'Sin agente', color: '#6b7280' };

  const crumb = sep([
    form.location_name || 'Sin ubicación',
    form.rack_name ? <b key="rack">{form.rack_name}</b> : 'Sin rack',
    rangeLabel || null,
    chassisName ? `Chasis ${chassisName}` : null,
    ip || null,
  ]);

  return (
    <>
      <div className="sf2-head">
        <div className="sf2-head-icon">
          <ServerStackIcon style={{ width: 24, height: 24 }} />
        </div>

        <div className="sf2-head-title">
          <div className="sf2-title-row">
            {inline ? (
              <input
                className="sf2-head-input"
                value={form.name || ''}
                onChange={(event) => onTitleChange?.(event.target.value)}
                onKeyDown={handleTitleKeyDown}
                placeholder="Nombre del servidor…"
                aria-label="Nombre del servidor"
                style={{ '--sf2-title-len': (form.name || '').length }}
              />
            ) : (
              <h1>{form.name || 'Nuevo servidor'}</h1>
            )}
            {showActions ? (
              <>
                <button
                  type="button"
                  className="sf2-title-btn sf2-title-btn--ok"
                  onClick={onSave}
                  disabled={saving || (inline ? !isDirty : false)}
                  title="Guardar cambios"
                >
                  <CheckIcon />
                </button>
                <button
                  type="button"
                  className="sf2-title-btn"
                  onClick={onDiscard}
                  disabled={saving}
                  title="Descartar cambios"
                >
                  <XMarkIcon />
                </button>
              </>
            ) : (
              <button
                type="button"
                className={inline ? 'sf2-title-btn sf2-title-pencil' : 'sf2-title-btn'}
                onClick={(event) => (inline
                  ? event.currentTarget.parentElement.querySelector('.sf2-head-input')?.focus()
                  : onEdit?.())}
                title="Editar la ficha"
                aria-label="Editar la ficha"
              >
                <PencilSquareIcon />
              </button>
            )}
          </div>
          <div className="sf2-crumb">{crumb}</div>
        </div>

        <div className="sf2-badges">
          <span className="sf2-badge" style={{ background: statusBg, color: statusColor, borderColor: 'transparent' }}>
            <i style={{ background: statusColor }} />
            {statusLabel}
          </span>
          <span className="sf2-badge">
            <i style={{ '--c': 'var(--amber-500, #f59e0b)' }} />
            {envLabel}
          </span>
          {isExisting && (
            <span
              className="sf2-badge"
              title={agentLastSeen ? `Última conexión: ${new Date(agentLastSeen).toLocaleString('es-ES')}` : 'Estado del agente'}
            >
              <i style={{ '--c': agenteInfo.color }} />
              {agenteInfo.label}
            </span>
          )}
          {agentUpdate?.label && (
            <span className="sf2-badge" title={agentUpdate.title || agentUpdate.label}>
              <i style={{ '--c': agentUpdate.color }} />
              {agentUpdate.label}
            </span>
          )}
          {form.os && <span className="sf2-badge">{form.os}</span>}
          {firstScanUi?.label && (
            <span className="sf2-badge" style={firstScanUi.style}>{firstScanUi.label}</span>
          )}
        </div>
      </div>

      <div className="sf2-chips">
        <button type="button" className="sf2-chip" onClick={() => onGo('organizacion')} title="Ver roles y organización">
          <b>{rolesCount}</b><span>Roles</span>
        </button>

        <button
          type="button"
          className={`sf2-chip${disksCount ? '' : ' sf2-chip--flat'}`}
          onClick={onOpenDisks}
          disabled={!isExisting}
          title={isExisting ? 'Abrir el inventario de discos' : 'Guarda el servidor para ver discos'}
        >
          <b>{disksCount}</b><span>Discos</span>
        </button>

        <button
          type="button"
          className={`sf2-chip${nicsCount ? '' : ' sf2-chip--flat'}`}
          onClick={() => onGo('red')}
          disabled={!isExisting}
          title={isExisting ? 'Ver tarjetas de red, equipos e IPs' : 'Guarda el servidor para ver la red'}
        >
          <b>{nicsCount}</b><span>NICs</span>
        </button>

        <button
          type="button"
          className={`sf2-chip${ipsCount ? '' : ' sf2-chip--flat'}`}
          onClick={() => onGo('red')}
          disabled={!isExisting}
          title={isExisting ? 'Ver las IPs asignadas' : 'Guarda el servidor para ver las IPs'}
        >
          <b>{ipsCount}</b><span>IPs</span>
        </button>

        <button
          type="button"
          className={`sf2-chip${accessCount ? '' : ' sf2-chip--flat'}`}
          onClick={() => onGo('access')}
          disabled={!isExisting}
          title={isExisting ? 'Ver los accesos (iDRAC, RDP, SSH…)' : 'Guarda el servidor para ver los accesos'}
        >
          <b>{accessCount}</b><span>Accesos</span>
        </button>

        <button
          type="button"
          className={`sf2-chip${usersCount ? '' : ' sf2-chip--flat'}`}
          onClick={onOpenUsers}
          disabled={!isExisting}
          title={isExisting ? 'Abrir los usuarios detectados' : 'Guarda el servidor para ver usuarios'}
        >
          <b>{usersCount || 0}</b><span>Usuarios</span>
        </button>

        {docButton}

        <button
          type="button"
          className={`sf2-chip${lunsCount ? '' : ' sf2-chip--flat'}`}
          onClick={() => onGo('almacenamiento')}
          disabled={!isExisting}
          title={isExisting ? 'Ver discos, RAID, volúmenes y LUNs' : 'Guarda el servidor para ver el almacenamiento'}
        >
          <b>{lunsCount}</b><span>LUNs</span>
        </button>
      </div>
    </>
  );
}
